import React, { useState } from 'react';
import { Box, Container, Typography, Paper, Button, CircularProgress, Alert } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import Filters from './Filters';
import ReportTable, { UsageReportRow } from './Table';
import { getUsageReport } from '../../api/UsageReport';
import {
  pageContainerSx,
  contentWrapperSx,
  titleBarSx,
  pageTitleSx,
  downloadButtonSx,
  errorAlertSx,
  filtersPaperSx,
  totalUsersBoxSx,
  loadingContainerSx,
  tablePaperSx
} from './styles';

/** Organizations that show Username and Email columns (must match backend COGNITO_ENRICH_ORGANIZATIONS). */
const ORGS_WITH_USER_DETAILS = ['novonordisk', 'abbvie', 'beone', 'lundbeck'];

function showUserDetailsForOrg(organization: string): boolean {
  const normalized = organization.trim().toLowerCase().replace(/\s+/g, '');
  return ORGS_WITH_USER_DETAILS.includes(normalized);
}

/** Parse company list from REACT_APP_DOMAIN_ORGANIZATION_MAP (JSON array). */
function getCompaniesFromEnv(): string[] {
  try {
    const raw = process.env.REACT_APP_DOMAIN_ORGANIZATION_MAP;
    if (!raw || typeof raw !== 'string') return [];
    const parsed = JSON.parse(raw.trim());
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item: unknown) => String(item))
      .filter(Boolean)
      .sort();
  } catch {
    return [];
  }
}

const companiesFromEnv = getCompaniesFromEnv();

const UsageReport: React.FC = () => {
  const [company, setCompany] = useState<string>('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [filteredData, setFilteredData] = useState<UsageReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const convertDateToISO = (dateString: string, isEndDate: boolean = false): string => {
    if (!dateString) return '';

    // Parse the date string (YYYY-MM-DD) and create a UTC date
    // For start date: add T00:00:00Z to start at midnight UTC
    // For end date: add T23:59:59Z to end at end of day UTC
    const timeComponent = isEndDate ? 'T23:59:59Z' : 'T00:00:00Z';
    const utcDateString = `${dateString}${timeComponent}`;

    const date = new Date(utcDateString);
    return date.toISOString();
  };

  // When company changes (user selects another organization), clear current dashboard data
  // so that the table becomes blank until a new search is performed.
  const handleCompanyChange = (value: string) => {
    setCompany(value);
    setFilteredData([]);
    setError('');
  };

  const fetchReportData = async (): Promise<void> => {
    setLoading(true);
    setError('');
    const fromISO = convertDateToISO(dateRange.from, false);
    const toISO = convertDateToISO(dateRange.to, true);
    const filters = { organization: company || '', from: fromISO, to: toISO };

    try {
      const response = await getUsageReport(filters);
      const payload = response?.data;
      const rows: UsageReportRow[] = Array.isArray(payload?.data) ? payload.data : [];
      setFilteredData(rows);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch usage report';
      setError(message);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    // Validate dates before searching
    if (!dateRange.from || !dateRange.to) {
      setError('Please select both From and To dates');
      return;
    }
    if (dateRange.from > dateRange.to) {
      setError('From date must be before or equal to To date');
      return;
    }
    // Manually trigger fetch with current filter values
    fetchReportData();
  };

  const handleResetFilters = () => {
    setCompany('');
    setDateRange({ from: '', to: '' });
    setFilteredData([]);
    setError('');
  };

  /** Escape a CSV cell per RFC 4180: double internal quotes, wrap in quotes. */
  const escapeCsvCell = (value: string | number | undefined | null): string => {
    const text = value === undefined || value === null ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };

  const convertToCSV = (data: UsageReportRow[]): string => {
    const showUserColumns = showUserDetailsForOrg(company);
    const statHeaders = ['Total Search Stats', 'Total Chat RIA Stats'];
    const headers = [
      ...(showUserColumns
        ? [
            'User ID',
            'Username',
            'Email',
            'Organization',
            'Last Used Date',
            'Total Time Spent (hrs)'
          ]
        : ['User ID', 'Organization', 'Last Used Date', 'Total Time Spent (hrs)']),
      'View Count',
      'Action Count',
      ...statHeaders
    ];

    const rows = data.map(row => {
      const base = [
        row.user_id,
        ...(showUserColumns ? [row.username ?? '', row.email ?? ''] : []),
        row.organization,
        new Date(row.last_used_date).toLocaleDateString('en-US', {
          timeZone: 'UTC',
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }),
        row.total_time_spent_hours.toFixed(2),
        String(row.total_view_count ?? 0),
        String(row.total_action_count ?? 0),
        (() => {
          const aria = row.aria_search_count ?? 0;
          const advance = row.advance_search_count ?? 0;
          const quick = row.quick_search_count ?? 0;
          const total = aria + advance + quick;
          return total > 0 ? String(total) : '';
        })(),
        row.chat_ria_search_count != null ? String(row.chat_ria_search_count) : ''
      ];
      return base;
    });

    const csvContent = [
      headers.map(h => escapeCsvCell(h)).join(','),
      ...rows.map(row => row.map(cell => escapeCsvCell(cell)).join(','))
    ].join('\n');

    return csvContent;
  };

  const downloadCSV = () => {
    const csvContent = convertToCSV(filteredData);
    const element = document.createElement('a');
    const file = new Blob([csvContent], { type: 'text/csv' });
    element.href = URL.createObjectURL(file);
    element.download = `usage-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(element);
    element.click();
    URL.revokeObjectURL(element.href);
    document.body.removeChild(element);
  };

  return (
    <Box sx={pageContainerSx}>
      <Container maxWidth='lg'>
        <Box sx={contentWrapperSx}>
          <Box sx={titleBarSx}>
            <Typography variant='h4' sx={pageTitleSx}>
              Usage Report
            </Typography>
            <Button
              variant='contained'
              color='success'
              startIcon={<DownloadIcon />}
              onClick={downloadCSV}
              disabled={filteredData.length === 0 || loading}
              sx={downloadButtonSx}>
              Download as CSV
            </Button>
          </Box>

          {error && (
            <Alert severity='error' sx={errorAlertSx}>
              {error}
            </Alert>
          )}

          <Paper elevation={1} sx={filtersPaperSx}>
            <Filters
              company={company}
              setCompany={handleCompanyChange}
              dateRange={dateRange}
              setDateRange={setDateRange}
              companies={companiesFromEnv}
              onSearch={handleSearch}
              onReset={handleResetFilters}
              loading={loading}
            />
          </Paper>

          {!loading && (
            <Box sx={totalUsersBoxSx}>
              <Typography variant='subtitle1' color='text.secondary'>
                Total users: {filteredData.length}
              </Typography>
            </Box>
          )}

          {loading ? (
            <Box sx={loadingContainerSx}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper elevation={2} sx={tablePaperSx}>
              <ReportTable data={filteredData} showUsernameEmail={showUserDetailsForOrg(company)} />
            </Paper>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default UsageReport;
